import get from "lodash/get";

import type {
    GraphDataEntity,
    GraphDataModifiedEntity,
    POPResponseEntity,
    POPSeries,
    SegmentsResponseEntity,
    SingleSegmentSeriesType,
    TotalGraphDataModifiedEntity,
} from "app/components/widget/widget-utilities/segments/types";
import type { ISeries } from "pro-features/components/Chart/src/components/PeriodOverPeriodChart/PoPChartTypes";
import { granularities } from "pro-features/utils";

import markerWithDashedConfig from "pro-features/components/Chart/src/configs/series/markerWithDashedLinePerPointChartConfig";

import { SegmentsMonthlyVisitsGraphWidget } from "./SegmentsMonthlyVisitsGraphWidget";
import type { DashboardMetric } from "./type";

export class SegmentsUniqueVisitorsGraphWidget extends SegmentsMonthlyVisitsGraphWidget {
    metric: DashboardMetric;

    constructor() {
        super();
        this.metric = "UniqueUsers";
    }

    static getWidgetDashboardType() {
        return "Graph";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    static getWidgetMetadataType() {
        return "SegmentsUniqueVisitorsGraph";
    }

    public fetchSegmentData = async () => {
        const { segmentId, compareFrom, timeGranularity, webSource } = this._params;
        const isPop = !!compareFrom;

        const timeGran = this.durationObject?.forAPI?.isWindow ? granularities[0] : granularities[2];

        const apiParams = {
            country: this._params.country,
            from: this.durationObject.forAPI.from,
            to: this.durationObject.forAPI.to,
            compareFrom: this.durationObject?.forAPI?.compareFrom,
            compareTo: this.durationObject?.forAPI?.compareTo,
            includeSubDomains: true,
            isWindow: this.durationObject?.forAPI?.isWindow,
            keys: null,
            segmentsIds: [segmentId],
            timeGranularity: timeGran,
            webSource: webSource,
            lastUpdated: this.currentSegmentsData[0].lastUpdated,
        };

        if (webSource === "Total") {
            const totalResponse = await this.segmentApiService.getCustomSegmentEngagementData(apiParams);

            if (isPop) {
                const _comparedInterval = get(
                    totalResponse,
                    `ComparedInterval[${segmentId}].${this.metric}.${timeGranularity}.Graph`,
                );

                const _interval = get(totalResponse, `Interval[${segmentId}].${this.metric}.${timeGranularity}.Graph`);

                const changeValue = (acc: Array<TotalGraphDataModifiedEntity>, val: GraphDataEntity) => {
                    acc.push({
                        ...val,
                        Value: {
                            ...val.Value,
                            Value: val.Value.Value,
                            Confidence: val.Value.Confidence,
                        },
                    });

                    return acc;
                };

                // @ts-ignore FIXME: strict migration - '_comparedInterval' is possibly 'undefined'
                const ComparedInterval = _comparedInterval.reduce(changeValue, []);
                // @ts-ignore FIXME: strict migration - '_interval' is possibly 'undefined'
                const Interval = _interval.reduce(changeValue, []);

                return {
                    Interval: {
                        [segmentId]: {
                            [this.metric]: { [timeGranularity]: { Graph: Interval } },
                        },
                    },
                    ComparedInterval: {
                        [segmentId]: {
                            [this.metric]: { [timeGranularity]: { Graph: ComparedInterval } },
                        },
                    },
                };
            }

            return totalResponse[segmentId]?.UniqueUsers[timeGranularity]?.Graph.map((item) => ({
                Key: item.Key,
                Value: { Value: item?.Value?.Value, Confidence: item?.Value?.Confidence },
            }));
        }

        return Promise.all([
            this.segmentApiService.getCustomSegmentEngagementData(apiParams),
            this.segmentApiService.getUniqueUsersData(apiParams),
        ]).then((res) => {
            if (isPop) {
                const _comparedInterval = get(
                    res[1],
                    `ComparedInterval[${segmentId}].${this.metric}.${timeGranularity}.Graph`,
                );

                const _interval = get(res[1], `Interval[${segmentId}].${this.metric}.${timeGranularity}.Graph`);

                const graph = get(res[0], `ComparedInterval[${segmentId}].Visits.Monthly.Graph`);

                const changeValue = (acc: Array<GraphDataModifiedEntity>, val: GraphDataEntity, index: number) => {
                    acc.push({
                        ...val,
                        Value: {
                            ...val.Value,
                            Value: val.Value,
                            Confidence: get(graph, `[${index}].Value.Confidence`),
                        },
                    });
                    return acc;
                };

                // @ts-ignore FIXME: strict migration - '_comparedInterval' is possibly 'undefined'
                const ComparedInterval = _comparedInterval.reduce(changeValue, []);
                // @ts-ignore FIXME: strict migration - '_interval' is possibly 'undefined'
                const Interval = _interval.reduce(changeValue, []);

                return {
                    Interval: {
                        [segmentId]: {
                            [this.metric]: { [timeGranularity]: { Graph: Interval } },
                        },
                    },
                    ComparedInterval: {
                        [segmentId]: {
                            [this.metric]: { [timeGranularity]: { Graph: ComparedInterval } },
                        },
                    },
                };
            }

            const uniqueUsersGraph = res[1][segmentId]?.UniqueUsers?.Monthly?.Graph;
            const visitsGraph = res[0][segmentId]?.Visits?.Monthly?.Graph;

            return uniqueUsersGraph.map((item, index) => ({
                Key: item.Key,
                Value: { Value: item?.Value, Confidence: visitsGraph[index]?.Value?.Confidence },
            }));
        });
    };

    public async getChartSeries(
        data: SegmentsResponseEntity | POPResponseEntity,
        compareItemsKeys?: string[],
    ): Promise<SingleSegmentSeriesType[] | POPSeries[]> {
        const { compareFrom } = this._params;
        const isPop = !!compareFrom;

        if (isPop) {
            return this.getPOPChartSeries(data as POPResponseEntity);
        }

        const config = markerWithDashedConfig({
            data: this.getFormattedChartData(data as unknown as GraphDataEntity[]),
            isDataSingleSeries: true,
        }) as SingleSegmentSeriesType;

        config.displayName = this.currentSegmentsData?.[0]?.domain;

        return [config];
    }

    public getPOPChartSeries(data: POPResponseEntity): POPSeries[] {
        const { segmentId, timeGranularity } = this._params;

        const comparedInterval = data.ComparedInterval[segmentId][this.metric][timeGranularity].Graph;
        const interval = data.Interval[segmentId][this.metric][timeGranularity].Graph;

        const comparePoPData: ISeries[] = this.getFormattedPoPChartData(interval, comparedInterval);

        return this.getPOPData(comparePoPData);
    }
}

SegmentsUniqueVisitorsGraphWidget.register();
